import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import CardList from '../../components/CardList';
import PageTitle from '../../components/PageTitle';
import EmptyState from '../../components/EmptyState';
import FloatingButton from '~/components/FloatingButton';
import Loading from '~/components/Loading';
import cardImages from '~/images/cardImages';

import { getTrainingRoutineById, getWorkoutsByTrainingRoutineId } from '~/api';

import EditMenu from './EditMenu';
import NewWorkoutDialog from './NewWorkoutDialog';

export default function TrainingRoutineDetails({ match, location }) {
  const {
    params: { userId, trainingRoutineId },
  } = match;
  const { state: trainingRoutineState } = location;

  const user = useSelector((state) => state.user.user);
  const isAdmin = user.role === 'ADMIN';

  const [isLoading, setIsLoading] = useState(true);
  const [trainingRoutine, setTrainingRoutine] = useState(trainingRoutineState);
  const [workouts, setWorkouts] = useState([]);
  const [newWorkoutDialogIsOpen, setNewWorkoutDialogIsOpen] = useState(false);
  const [editWorkoutId, setEditWorkoutId] = useState(null);

  // Handle the edit menu from each workout card
  const [anchorEl, setAnchorEl] = useState(null);
  const editMenuIsOpen = !!anchorEl;

  const handleOpenMenu = (event, workout) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setEditWorkoutId(workout.id);
  };

  const history = useHistory();
  const currentURL = `/users/${userId}/training-routines/${trainingRoutineId}`;
  const onWorkoutClick = (workout) => {
    history.push(`${currentURL}/workout/${workout.id}`, {
      name: workout.title,
    });
  };

  const getTrainingRoutine = async () => {
    const { data: trainingRoutineResponse } = await getTrainingRoutineById(
      trainingRoutineId
    );
    setTrainingRoutine(trainingRoutineResponse);
  };

  const getWorkouts = async () => {
    try {
      const { data: workoutsResponse } = await getWorkoutsByTrainingRoutineId(
        trainingRoutineId
      );
      setWorkouts(workoutsResponse);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!trainingRoutineState) {
      getTrainingRoutine();
    }
    getWorkouts();
  }, [trainingRoutineId]);

  const workoutsList = workouts.map((workout, i) => {
    return {
      id: workout.id,
      title: workout.name,
      subtitle: workout.observations,
      backgroundImage: cardImages[i % cardImages.length],
    };
  });

  return (
    <>
      {trainingRoutine ? (
        <>
          <PageTitle>{trainingRoutine.title}</PageTitle>
          {!isLoading ? (
            <CardList
              items={workoutsList}
              emptyStateText="Não há nenhuma ficha neste treino ainda!"
              onEdit={handleOpenMenu}
              onSelect={onWorkoutClick}
              isAdmin={isAdmin}
            />
          ) : (
            <Loading />
          )}
        </>
      ) : (
        <EmptyState
          text="Treino não encontrado!"
          style={{ marginTop: '20px' }}
        />
      )}

      {/* Prevents the component to be rended without a editWorkoutId */}
      {editWorkoutId && (
        <EditMenu
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          isOpen={editMenuIsOpen}
          workoutId={editWorkoutId}
          refreshWorkouts={getWorkouts}
        />
      )}

      {isAdmin && (
        <>
          <FloatingButton onClick={() => setNewWorkoutDialogIsOpen(true)} />
          <NewWorkoutDialog
            isOpen={newWorkoutDialogIsOpen}
            setIsOpen={setNewWorkoutDialogIsOpen}
            trainingRoutineId={trainingRoutineId}
            orderedIndex={workoutsList.length}
            refreshWorkouts={getWorkouts}
          />
        </>
      )}
    </>
  );
}

TrainingRoutineDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      userId: PropTypes.number,
      trainingRoutineId: PropTypes.number,
    }),
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      trainingRoutineState: PropTypes.object,
    }),
  }).isRequired,
};
